import React, { useEffect, useState, useMemo } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import axios from "axios"
import apiURl from "url"
import ExcelDownload from "components/Common/ExcelDownload"

const BusinessServiceTypes = () => {
  document.title = "Service Types"

  const [serviceTypes, setServiceTypes] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editID, setEditID] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  })

  // Fetch all service types
  const fetchServiceTypes = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(`${apiURl}/fetchServiceTypes`)
      if (response.data.status === "success") {
        setServiceTypes(response.data.services)
      } else {
        toastr.error(response.data.message || "Failed to fetch services")
      }
    } catch (error) {
      toastr.error("Error fetching service types")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchServiceTypes()
  }, [])

  // Handle input
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  // Reset form
  const resetForm = () => {
    setFormData({ name: "", description: "" })
    setEditID(null)
    setModalOpen(false)
  }

  // Save / Update
  const handleSubmit = async e => {
    e.preventDefault()
    if (!formData.name) {
      toastr.error("Service Name is required")
      return
    }

    try {
      const URL = editID
        ? `${apiURl}/updateServiceType`
        : `${apiURl}/addServiceType`
      const payload = editID ? { id: editID, ...formData } : formData
      const response = await axios.post(URL, payload)

      if (response.data.status === "success") {
        toastr.success(
          editID ? "Service updated successfully" : "Service added successfully"
        )
        resetForm()
        fetchServiceTypes()
      } else {
        toastr.error(response.data.message || "Failed to save service")
      }
    } catch (error) {
      toastr.error("Error saving service")
    }
  }

  // Edit service
  const handleEdit = service => {
    setFormData({ name: service.name, description: service.description || "" })
    setEditID(service.id)
    setModalOpen(true)
  }

  // Delete service
  const handleDelete = async id => {
    try {
      const response = await axios.post(`${apiURl}/deleteServiceType`, { id })
      if (response.data.status === "success") {
        toastr.success("Service deleted successfully")
        fetchServiceTypes()
      } else {
        toastr.error(response.data.message || "Failed to delete service")
      }
    } catch (error) {
      toastr.error("Error deleting service")
    }
  }

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Service Name",
        accessor: "name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: cellProps => <span>{cellProps.value || "-"}</span>,
      },
      {
        Header: "Action",
        accessor: "id",
        disableFilters: true,
        Cell: cellProps => (
          <div className="d-flex gap-2">
            <Button
              color="warning"
              size="sm"
              onClick={() => handleEdit(cellProps.row.original)}
            >
              Edit
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDelete(cellProps.value)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [serviceTypes]
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <h5 className="mt-2">Manage Service Types</h5>
          </Col>
          <Col className="text-end">
            <Button
              color="primary"
              className="btn-sm w-md"
              onClick={() => setModalOpen(true)}
            >
              + Add Service
            </Button>
          </Col>
        </Row>

        <Row className="pt-3">
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <div className="float-end d-flex gap-2">
                      <ExcelDownload
                        leads={serviceTypes}
                        title="Service Types"
                      />
                    </div>
                    <TableContainer
                      columns={columns}
                      data={serviceTypes}
                      customPageSize={10}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        centered
      >
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
            {editID ? "Edit Service" : "Add New Service"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Service Name *</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={resetForm}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editID ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  )
}

export default BusinessServiceTypes
